import React, { ReactChild, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

import { ActionItem, ActionList, BackgroundTypes, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '.';
import Button from '../button';
import { closeModal } from '.';
import LoadingSpinner from '../loading-spinner';
import * as typography from '../typography';

export function ErrorModal({ title, ...restOfProps }: Parameters<typeof ErrorMessageComponent>[0] & { title: string }) {
  return (
    <Template
      actions={<CloseButton />}
      content={<ErrorMessageComponent {...restOfProps} />}
      title={<HeaderTitle type="error">{title}</HeaderTitle>}
    />
  );
}

export function ErrorMessageComponent({ error, instructions }: Record<'error' | 'instructions', string | JSX.Element>) {
  return (
    <MessageContainerDiv>
      <ContentHeader>{error}</ContentHeader>
      {instructions}
    </MessageContainerDiv>
  );
}

const MessageContainerDiv = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

export function ContentHeader(props: Parameters<typeof typography.Block>[0]) {
  return <typography.Block {...props} size="lg" weight="bold" />;
}

export function LoadingModal() {
  return <Template content={loadingMessageElement} title="Processing Request" />;
}

const LoadingTextDiv = styled(typography.Text)`
  margin-top: 1rem;
  text-align: center;
`;

export const loadingMessageElement = (
  <MessageContainerDiv>
    <LoadingSpinner />
    <LoadingTextDiv size="sm" weight="medium" as="p">
      Please wait
    </LoadingTextDiv>
  </MessageContainerDiv>
);

export function Template({
  actions,
  content,
  title
}: {
  actions?: ReactChild;
  content: ReactChild;
  title: ReactElement | string;
}) {
  return (
    <>
      {typeof title === 'string' ? <HeaderTitle>{title}</HeaderTitle> : title}
      <ModalContent includePadding withFooter={Boolean(actions)}>
        {content}
        {actions && (
          <ModalFooter>
            <ActionList>{actions}</ActionList>
          </ModalFooter>
        )}
      </ModalContent>
    </>
  );
}

export function HeaderTitle({ children, type }: { children: ReactNode; type?: BackgroundTypes }) {
  return (
    <ModalHeader {...{ type }}>
      <ModalTitle {...{ type }}>{children}</ModalTitle>
    </ModalHeader>
  );
}

export function CloseButton({ children }: { children?: ReactNode }) {
  return (
    <ActionButton modifiers="secondaryRound" onClick={closeModal}>
      {children || 'Close'}
    </ActionButton>
  );
}

export function ActionButton(props: Parameters<typeof Button>[0]) {
  return (
    <ActionItem data-testid="modal-action-item">
      <Button {...props} />
    </ActionItem>
  );
}
